import { FC } from 'react'

import styled from 'styled-components'

import { getIsLanguageActive } from 'src/Pages/Artist/SpotifySchedulePage/helpers/helpers'
import { useAppSelector } from 'src/Hooks/redux'
import { useCheckoutEvents } from 'src/Hooks/useCheckoutEvents'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { ScheduleSteps } from 'src/Types/ui'
import { spotifyTotalPriceSelector } from 'src/Redux/spotifyCampaignMatchingProcess'
import { useSpotifyCampaignMatching } from 'src/Redux/spotifyCampaignMatchingApi'
import { CTAButton } from 'src/Components/Buttons/CTAButton'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'
import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    gap: 0;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    padding: 8px 16px;
    background: ${({ theme }) => theme.color.white};
    border-top: 1px solid ${({ theme }) => theme.color.lightGrey};
  }
`

const Text = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  text-align: center;
  font-family: 'Segoe UI';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`

const StyledCTAButton = styled(CTAButton)`
  padding-left: 40px;
  padding-right: 40px;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    width: 100%;
  }
`

const isCampaignSettingsIncomplete = (
  isPreRelease: boolean,
  hasReleaseDate: boolean,
  hasTrackFile: boolean,
  hasGenres: boolean,
  isLanguageActive: boolean,
  hasAiUsage: boolean,
): boolean => {
  if (!hasGenres || isLanguageActive) {
    return true
  }
  return isPreRelease && (!hasReleaseDate || !hasTrackFile || !hasAiUsage)
}

export const SpotifySettingsContinueButton: FC = () => {
  const { handleCheckoutStartEvent } = useCheckoutEvents()

  const spotifyTotalPrice = useAppSelector(spotifyTotalPriceSelector)
  const { isFetching, maxCuratorsCount } = useSpotifyCampaignMatching()
  const isUpdateCampaignLoading = isFetching || maxCuratorsCount === null
  const { campaignDetails, isPreRelease, releaseDate, trackFile } =
    useSpotifyCampaignStatus()

  const { setSearch } = useCustomSearchParams()

  if (!campaignDetails) {
    return null
  }

  const isLanguageActive = getIsLanguageActive(campaignDetails.languages)
  if (
    isCampaignSettingsIncomplete(
      isPreRelease,
      Boolean(releaseDate),
      Boolean(trackFile),
      Boolean(campaignDetails.genres.length),
      isLanguageActive,
      Boolean(campaignDetails.aiUsage),
    )
  ) {
    return null
  }
  const handleClick = (): void => {
    setSearch({ step: ScheduleSteps.CHECKOUT })

    spotifyTotalPrice &&
      handleCheckoutStartEvent({
        platformName: CampaignPlatformTypeEnum.SPOTIFY,
        id: Number(campaignDetails.id),
        campaignType: campaignDetails.type,
        genres: campaignDetails.genres.map((genre) => genre.name),
        languages: campaignDetails.languages.map((language) => language.name),
        startDate: (campaignDetails.start_date || '').toString(),
        resultPrice: spotifyTotalPrice,
      })
  }
  const hasPrice = spotifyTotalPrice && spotifyTotalPrice > 0

  const isDisabled = !hasPrice || isUpdateCampaignLoading

  return (
    <Container>
      <ButtonWrapper>
        <StyledCTAButton onClick={handleClick} disabled={isDisabled}>
          Continue
        </StyledCTAButton>
        <Text>You won`t be charged yet</Text>
      </ButtonWrapper>
    </Container>
  )
}
